/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * VisRenderSupport
 * INTENT: VisRenderSupport - implements the portion of the VisAPI that is focused on rendering or reacting to a model change
 *								(rather than updating the model).
 */
define([
	'jquery',
	'../../../../data/models/VisPropertyCollection',
	'../../../../lib/@waca/core-client/js/core-client/ui/core/Events'
], function($, VisPropertyCollection, Events) {
	const VisRenderSupport = Events.extend({

		init: function(attributes) {
			//Initialize a minimal set of attributes (nested collections from spec etc)
			VisRenderSupport.inherited('init', this, arguments);

			this.content = attributes.content;

			this.visModelManager = attributes.visModelManager;
			this.ownerWidget = attributes.ownerWidget;
			this.pageContext = attributes.pageContext;
			this.synchronizeData = attributes.synchronizeData;
			this.dashboardApi = attributes.dashboardApi;
			this.transactionApi = this.dashboardApi.getFeature('Transaction');

			this._registeredActions = {};

			this.content.on('change:state:status', this._onChangeStatus, this);
			this.content.on('change:state:error', this._onChangeError, this);


			// TODO: Once we merge with data widget model and vis model we won't have to do this anymore..
			if (this.ownerWidget) {
				//Subscribe to ownerWidget widget events
				this.ownerWidgetEvents = {
					'dwChange:refreshTimerIndicator': this.onChangeRefreshTimerIndicator,
					'dwChange:annotations': this.onChangeAnnotations,
					'dwChange:showTitle': this.onShowTitle,
					'dwChange:titleMode': this.onShowTitle,
					'dwChange:customData':this.onChangeCustomData
				};
				for (var key in this.ownerWidgetEvents) {
					this.ownerWidget.on(key, this.ownerWidgetEvents[key], this);
				}
			}
		},

		//TODO: Temporarily access the render sequence through visModelManager.
		getRenderSequence: function() {
			const controller =  this.content.getFeature('InteractivityController.deprecated');
			return controller ? controller.getRenderSequence() : null;
		},

		getDefinition: function() {
			return this.visModelManager.getDefinition();
		},

		resize: function(newWidth, newHeight, renderOptions = {}) {
			// Ensure that the computed height and width for '.widgetContent' are positive values
			if ((newWidth <= 0) || (newHeight <= 0)) {
				return Promise.resolve();
			}

			const lastSizeRendered = this.getRenderSequence().getLastSizeRendered() || {};

			/* If there is no lastSizeRendered, then we consider height & width changed or is a new render state*/
			const tolerance = 1.0;
			const bIsWidthChanged = (!lastSizeRendered.width) || (Math.abs(lastSizeRendered.width - newWidth) >= tolerance);
			const bIsHeightChanged = (!lastSizeRendered.height) || (Math.abs(lastSizeRendered.height - newHeight) >= (tolerance + 2.0));
			const forceReRender = renderOptions && renderOptions.reRender === true;
			const bAlwaysRender = this.ownerWidget._currVis && this.ownerWidget._currVis.alwaysRenderOnResize;
			const shouldRender = forceReRender || bAlwaysRender || bIsHeightChanged || bIsWidthChanged;

			// We are using properties api for resizing in LW
			// so we don't need another re-render by resize event
			// However, there are two expections:
			//     1. explore needs to trigger resize event when resizing the size panels
			//     2. group widget needs to trigger reisze event for its sub-contents
			// So we will only re-render if others are calling resize.
			if (shouldRender && renderOptions.modelId !== this.ownerWidget.id) {
				const transactionToken = renderOptions && renderOptions.data && renderOptions.data.transactionToken || {};
				return this.transactionApi.registerTransactionHandler(transactionToken, this.ownerWidget.id, function() {
					return this.getRenderSequence().reRender({
						resizing: true,
						...renderOptions
					});
				}.bind(this), renderOptions);
			} else {
				return Promise.resolve();
			}
		},

		_onChangeError: function() {
			this.ownerWidget.removeLoadingAnimation();
		},

		_getQueryRefresh: function() {
			if (this.queryRefreshProp === undefined) {
				const refreshProperty = this.content.getPropertyValue('queryRefresh');
				this.queryRefreshProp =  refreshProperty && refreshProperty.autoRefresh;
			}
			return this.queryRefreshProp;
		},

		setQueryRefresh: function(queryRefresh) {
			this.queryRefreshProp = queryRefresh;
		},

		_onChangeStatus: function(event) {
			const isRefresh = this._getQueryRefresh();
			// Only show the loading animation when we are not a auto refresh mode.
			if(!isRefresh){
				if (event.info.value === 'rendering'){
					this.ownerWidget.showLoadingAnimation();
				} else {
					this.ownerWidget.removeLoadingAnimation();
				}
			}

		},

		reRender: function(extraInfo) {
			return this.getRenderSequence().reRender({
				refresh: {
					data: true
				},
				extraInfo: extraInfo
			});
		},

		mapVizRenderForPrint: function(){
			return this.getRenderSequence().reRender({
				extraInfo: { preserveDrawingBuffer:true }
			});
		},

		//========================================================================================================

		initialize: function() {
			if (this.properties) {
				// inject the visModel into the properties collection
				this.properties.visModel = this;
			} else {
				this.properties = new VisPropertyCollection([], {
					visModel: this
				});
			}

			this._suppressViewAnimations = false;
		},

		getVisId: function() {
			return (this.getDefinition()) ? this.getDefinition().id : null;
		},

		getPreferredSize: function() {
			return this.getDefinition().preferredSize;
		},

		remove: function() {
			this.clear();
		},

		clear () {
			this._cleanUpEventsHandling();
		},

		_cleanUpEventsHandling () {

			this.content.off('change:state:status', this._onChangeStatus, this);
			this.content.off('change:state:error', this._onChangeError, this);
			// cleanup external events
			for (var key in this.ownerWidgetEvents) {
				this.ownerWidget.off(key, this.ownerWidgetEvents[key], this);
			}

			// cleanup registered actions (eg sortAction, filterAction).
			Object.keys(this._registeredActions).forEach((action) => {
				delete this._registeredActions[action];
			});
		},

		/**
		 * @returns a specification for this particular visualization subtype
		 * as a path to a template file.
		 * (In the case of RAVE, this would be the RAVE template specification for the
		 *  specific visualization...eg: bubblechart.json or bars.json)
		 */
		getSpecification: function() {
			return this.getDefinition() ? this.getDefinition().specification : null;
		},

		/**
		 * @returns the renderer for this VisRenderSupport as a path and name as defined in vis definitions.
		 *                   (see: smart-viz/ws/src/main/webapp/WEB-INF/localGallery)
		 */
		getRenderer: function() {
			return this.getDefinition() ? this.getDefinition().renderer : null;
		},

		/**
		 * Since the render process can be asynch, this method exists to allow views
		 * to notify their model (and, in turn the widget) that render is complete.
		 * This is useful for test purposes.  A test can override the widget method to know
		 * that an action is completed prior to proceeding.
		 *
		 * @param {object} renderInfo - additional rendering information
		 */
		renderComplete: function(renderInfo) {
			if (this.ownerWidget) {
				this.ownerWidget._firstRenderComplete = true;
				this.ownerWidget.renderComplete(renderInfo);
			}
		},

		/**
		 * Similar to renderComplete this function gets called when render is done, however before the
		 * animations begin.
		 */
		renderCompleteBeforeAnimation: function() {
			this.ownerWidget.removeLoadingAnimation();
		},

		/**
		 * TEMPORARY METHOD: The VisCanvas is being deprecated but is still created in the original board.
		 * Until the canvas is completely removed, callers can call getCanvas() as normal
		 * and will either return
		 * 1) the canvas object (if a VisCanvas has been created)
		 * 2) a temporary object with a getDataSource() method that returns the dataSet member of visModel.
		 */
		getCanvas: function() {
			if (this.canvas) {
				return this.canvas;
			}
			return {
				dataSet: this.dataSet,
				getDataSource: function() {
					return this.dataSet;
				}
			};
		},

		_notifyViews: function(eventName, payload) {
			this.visModelManager.trigger(eventName, payload);
		},

		/**
		 * @param event - the event (sent from the widget) when the data changes.
		 * @returns a Promise which is resolved when rendering can continue
		 *			(normally, that means immediately, but, in the case where metadata needs
		 *			to be loaded, rerendering continues only after the metadata is available.)
		 * //TODO: This handler is deprecated and should not be used.
		 */
		onChangeDataSlot: function(event) {
			var metadataAPI=this.visModelManager.getMetadataAPI();
			if (metadataAPI && !metadataAPI.getModule()) {
				//If undo causes all data to be removed (after create from visId), redo causes the visualization
				//to be recreated,  In this case, the module must be loaded on the first change to data.
				return this.ownerWidget.loadMetadata()
					.finally(()=>{
						this._notifyViews('change:dataSlot', {
							eventName: event.eventName,
							name: 'dataSlot',
							value: event.sender,
							sender: this,
							changeEvent: event
						});
					});
			} else {
				this._notifyViews('change:dataSlot', {
					eventName: event.eventName,
					name: 'dataSlot',
					value: event.sender,
					sender: this,
					changeEvent: event
				});
				return Promise.resolve();
			}
		},

		onChangeAnnotations: function(event) {
			this._notifyViews('change:annotations', {
				eventName: event.eventName,
				value: event.sender,
				sender: this,
				changeEvent: event
			});
		},

		/**
		 * This method will trigger an event to alert interested entities when the showTitle property has been toggled.
		 */
		onShowTitle: function(event) {
			this._notifyViews('change:titleMode', {
				name: 'change:titleMode',
				sender: this,
				value: event.value,
				eventName: 'change:theme'
			});
		},

		onChangeCustomData: function(event) {
			this._notifyViews('change:customData', {
				name: 'change:customData',
				sender: this,
				value: event.value,
				prevValue: event.prevValue,
				eventName:'change:customData'
			});
		},

		/**
		 * This method will trigger an event to alert interested entities when the theme has been
		 * updated. It is expected that the theme definition of ownerWidget has been updated before
		 * this method is called.
		 */
		onChangeTheme: function() {
			this._notifyViews('change:theme', {
				name: 'change:theme',
				sender: this,
				eventName:'change:theme'
			});
		},

		getContextToolbarItems: function() {
			var items = [];
			this.actions.forEach((action) => {
				items = items.concat(action().getAvailableActions());
			});
			return items;
		},

		setSuppressViewAnimations: function(suppress) {
			this._suppressViewAnimations = suppress;
		},

		getSuppressViewAnimations: function() {
			return this._suppressViewAnimations;
		},

		onChangeRefreshTimerIndicator: function(event) {
			this._notifyViews('change:refreshTimer', event);
		},

		doLocalSort: function() {
			return (this.getDefinition() && this.getDefinition().clientSort);
		}
	});

	return VisRenderSupport;
});
